import React from "react"
import { FormProvider, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { Platform } from "react-native"

import {
  completeSmsVerificationAsync,
  generateSmsVerificationCodeAsync,
} from "@/base/farmhq-api"
import Form from "@/components/Form"
import { useRequestHelpers } from "@/hooks/useRequestHelpers"
import { useIsPending } from "@/redux/requests.reducer"
import type { BoxProps } from "@/theme"
import {
  AlertCard,
  AlertDialog,
  AlertDialogScrollView,
  Box,
  SubmitButton,
} from "@/theme"
import { ActionButtons } from "./ActionButtons"
import { CodeStringField } from "./Auth"

interface Props {
  onClose: (() => void) | null
  upnId: number
}

/**
 * Allow user to verify the phone number using a one-time-code
 *
 * NOTE: this component does not automatically send the code.
 */

export function VerifyPhoneNumberForm({
  onClose,
  upnId,
  ...props
}: BoxProps & Props) {
  const { t } = useTranslation("phoneNumbers")

  const form = useForm({ defaultValues: { codeString: "" } })

  const { dispatch, handleError, isLoading, toasts } = useRequestHelpers(
    "CompleteSmsVerification",
  )
  const isCodeLoading = useIsPending("GenerateSmsVerificationCode")

  const handleSubmitCode = form.handleSubmit(({ codeString }) => {
    dispatch(completeSmsVerificationAsync({ codeString, upnId }))
      .unwrap()
      .then((response) => {
        if (response.status === "INVALID") {
          // Show invalid code message
          return form.setError("codeString", {
            message: t("codeInvalid", { ns: "validation" }),
          })
        }
        // Success case
        toasts.success()
        if (Platform.OS !== "web" && onClose) {
          onClose()
        }
        return response
      })
      .catch((error) => {
        handleError(error, { toastMessage: "default" })
      })
  })
  const handleResendCode = () => {
    dispatch(generateSmsVerificationCodeAsync({ upnId }))
      .unwrap()
      .then(() => toasts.info(t("codeSent", { ns: "common" })))
      .catch((error) => {
        handleError(error, { toastMessage: "default" })
      })
  }
  return (
    // CodeStringField requires control available in context
    <FormProvider {...form}>
      <Box id={"verifyPhoneNumber"} {...props}>
        <AlertCard
          bodyText={t("verifyInstructions", { ns: "phoneNumbers" })}
        />
        <Box my={"$4"}>
          <CodeStringField
            labelText={t("otpFieldLabel", { ns: "common" })}
            _button={{
              IconComponent: "Refresh",
              isDisabled: isLoading,
              isLoading: isCodeLoading,
              onPress: handleResendCode,
            }}
            _input={{
              style: {
                minWidth: 160,
              },
            }}
          />
        </Box>
        <Box mt={"$4"}>
          {onClose ? (
            <ActionButtons
              isDisabled={isCodeLoading}
              isLoading={isLoading}
              orientation={"vertical"}
              onPressCancel={onClose}
              onPressSubmit={handleSubmitCode}
            />
          ) : (
            <SubmitButton
              isDisabled={isCodeLoading}
              isLoading={isLoading}
              onPress={handleSubmitCode}
            />
          )}
        </Box>
      </Box>
    </FormProvider>
  )
}

export default function VerifyPhoneNumberDialog({
  onClose,
  upnId,
}: {
  onClose: () => void
  upnId: number | null | undefined
}) {
  const { t } = useTranslation("phoneNumbers")

  if (typeof upnId === "number") {
    return (
      <AlertDialog
        isOpen
        IconComponent={"VerifyPhoneNumber"}
        titleElement={t("verifyTitle")}
        onClose={onClose}
      >
        <AlertDialogScrollView>
          <Form>
            <VerifyPhoneNumberForm upnId={upnId} onClose={onClose} />
          </Form>
        </AlertDialogScrollView>
      </AlertDialog>
    )
  }
  return null
}
